import { AmsPermissionClaimType, authService } from '../../services/Auth';
import {
    AuspiceDto,
    Institution as InstitutionModel,
    InstitutionTypeDto,
    InstitutionalAccreditor,
} from '../../common/Types';
import { Button, FormError, Input, InstitutionNameHistory } from '../../components';
import { Card, CardBody, CardFooter, Col, Form, FormGroup, Label, Modal, Row } from 'reactstrap';
import React, { useEffect, useState } from 'react';
import moment from 'moment';

import { Redirect } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { formatPhoneNumber, parseUserInfo } from '../../common/Utils';
import { institutionService } from '../../services/InstitutionService';
import { systemAdministration } from '../../services/SystemAdministration';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { Countries, States } from '../../common/Data';

const Institution = (props) => {
    const [editing, setEditing] = useState(false);
    const {
        handleSubmit,
        control,
        watch,
        reset,
        formState: { errors },
    } = useForm();
    const [institution, setInstitution] = useState<InstitutionModel>(props.institution);
    const [institutionTypes, setInstitutionTypes] = useState<InstitutionTypeDto[]>();
    const [accreditors, setAccreditors] = useState<InstitutionalAccreditor[]>();
    const [auspices, setAuspices] = useState<AuspiceDto[]>();

    const [showNameHistory, setShowNameHistory] = useState<boolean>(false);
    const toggleShowNameHistory = () => setShowNameHistory(!showNameHistory);

    const [showRemove, setShowRemove] = useState<boolean>(false);
    const toggleShowRemove = () => setShowRemove(!showRemove);
    const [redirectToList, setRedirectToList] = useState<boolean>(false);
    const [isInternational, setIsInternational] = useState<boolean>(false);

    const country = watch('country');

    useEffect(() => {
        if (country && country !== 'United States of America') {
            setIsInternational(true);
        } else {
            setIsInternational(false);
        }
    }, [country]);

    useEffect(() => {
        setInstitution(props.institution);

        const loadTypes = () => {
            institutionService
                .getTypes()
                .then((types) => {
                    setInstitutionTypes(
                        types.filter((t) => t.isActive === true).sort((a, b) => a.title.localeCompare(b.title)),
                    );
                })
                .catch(() => {
                    toast.error('Error loading institution types');
                });
        };

        const loadAuspices = () => {
            institutionService
                .getAuspice()
                .then((aus) => {
                    setAuspices(aus.filter((a) => a.isActive === true).sort((a, b) => a.title.localeCompare(b.title)));
                })
                .catch(() => {
                    toast.error('Error loading auspices');
                });
        };

        const loadAccreditors = () => {
            systemAdministration
                .getInstitutionalAccreditors()
                .then((results) => {
                    setAccreditors([
                        ...results.filter((a) => a.isActive === true).sort((a, b) => a.title.localeCompare(b.title)),
                    ]);
                })
                .catch(() => {
                    toast.error('Error loading institutional accreditors');
                });
        };

        loadTypes();
        loadAuspices();
        loadAccreditors();
    }, [props.institution]);

    const onSubmit = (formData) => {
        const toastId = toast.info('Saving institution...');
        institutionService
            .saveInstitutions(new InstitutionModel(formData))
            .then((response) => {
                setInstitution(response);
                setEditing(false);
                toast.update(toastId, {
                    type: 'success',
                    render: 'Institution Saved.',
                });
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save institution.',
                });
            });
    };

    const onDeactivate = () => {
        const toastId = toast.info('Deactivating institution...');
        institutionService
            .saveInstitutions({
                ...institution,
                active: false,
            })
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Institution deactivated successfully',
                });
                setRedirectToList(true);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error encountered deactivating institution',
                });
            });
    };

    const editedByInfo = parseUserInfo(institution.editedByString);

    if (redirectToList) {
        return <Redirect to={`/institutions`} />;
    } else {
        return (
            <>
                {!institution && (
                    <>
                        <div>
                            <Skeleton count={2} />
                        </div>
                        <div>
                            <Skeleton count={2} />
                        </div>
                        <div>
                            <Skeleton count={2} />
                        </div>
                        <div>
                            <Skeleton count={2} />
                        </div>
                        <div>
                            <Skeleton count={2} />
                        </div>
                    </>
                )}
                {institution && (
                    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                        {!editing && (
                            <FormGroup>
                                <Label>{`Insitution Id`}</Label>
                                <div>{institution.institutionId}</div>
                            </FormGroup>
                        )}
                        <FormGroup>
                            <Label for="title">
                                <span>{`Name`}</span>
                                {!editing && (
                                    <Button
                                        className={'ml-3 btn-sm'}
                                        type={'button'}
                                        onClick={() => toggleShowNameHistory()}
                                    >
                                        <i className={'mdi mdi-history'} />
                                        <span className={'ml-1'}>{`View Name History`}</span>
                                    </Button>
                                )}
                            </Label>
                            {editing && (
                                <>
                                    <Controller
                                        name="title"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                id="title"
                                                placeholder="Name"
                                                {...field}
                                                value={field.value ?? ''}
                                            />
                                        )}
                                        defaultValue={institution.title ?? ''}
                                    />
                                    <FormError errors={errors} name="title" message="Name is required" />
                                </>
                            )}
                            {!editing && (
                                <div>
                                    <span>{institution.title}</span>
                                </div>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="institutionTypeId">Type</Label>
                            {editing && (
                                <Controller
                                    name="institutionTypeId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="select"
                                            id="institutionTypeId"
                                            {...field}
                                            value={field.value ?? ''}
                                        >
                                            {institutionTypes &&
                                                institutionTypes.map((t) => (
                                                    <option key={t.institutionTypeId} value={t.institutionTypeId}>
                                                        {t.title}
                                                    </option>
                                                ))}
                                        </Input>
                                    )}
                                    defaultValue={institution.institutionTypeId ?? ''}
                                />
                            )}
                            {!editing && (
                                <div>
                                    {
                                        institutionTypes?.find(
                                            (t) => t.institutionTypeId === institution.institutionTypeId,
                                        )?.title
                                    }
                                </div>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="auspiceId">Control</Label>
                            {editing && (
                                <Controller
                                    name="auspiceId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input type="select" id="auspiceId" {...field} value={field.value ?? ''}>
                                            {auspices &&
                                                auspices.map((a) => (
                                                    <option key={a.auspiceID} value={a.auspiceID}>
                                                        {a.title}
                                                    </option>
                                                ))}
                                        </Input>
                                    )}
                                    defaultValue={institution.auspiceId ?? ''}
                                />
                            )}
                            {!editing && (
                                <div>{auspices?.find((a) => a.auspiceID === institution.auspiceId)?.title}</div>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="institutionalAccreditorId">Institutional Accreditor</Label>
                            {editing && (
                                <Controller
                                    name="institutionalAccreditorId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="select"
                                            id="institutionalAccreditorId"
                                            {...field}
                                            value={field.value ?? ''}
                                        >
                                            <option value={-1}>{`Not Applicable`}</option>
                                            {accreditors &&
                                                accreditors.map((a) => (
                                                    <option
                                                        key={a.institutionalAccreditorId}
                                                        value={a.institutionalAccreditorId}
                                                    >
                                                        {a.title}
                                                    </option>
                                                ))}
                                        </Input>
                                    )}
                                    defaultValue={institution.institutionalAccreditorId ?? ''}
                                />
                            )}
                            {!editing && (
                                <div>
                                    {
                                        accreditors?.find(
                                            (a) =>
                                                a.institutionalAccreditorId === institution.institutionalAccreditorId,
                                        )?.title
                                    }
                                </div>
                            )}
                        </FormGroup>
                        <div>
                            {editing && (
                                <div className="d-flex" style={{ gap: '0.5rem' }}>
                                    <div style={{ flex: '2' }}>
                                        <FormGroup>
                                            <Label for="phone">Phone</Label>
                                            <Controller
                                                name="phone"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="text"
                                                        id="phone"
                                                        placeholder="##########"
                                                        {...field}
                                                        value={field.value ?? ''}
                                                    />
                                                )}
                                                defaultValue={institution.phone ?? ''}
                                            />
                                        </FormGroup>
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <FormGroup>
                                            <Label for="extension">Ext</Label>
                                            <Controller
                                                name="extension"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="text"
                                                        id="extension"
                                                        placeholder="####"
                                                        {...field}
                                                        value={field.value ?? ''}
                                                    />
                                                )}
                                                defaultValue={institution.extension ?? ''}
                                            />
                                        </FormGroup>
                                    </div>
                                    <div style={{ flex: '2' }}>
                                        <FormGroup>
                                            <Label for="fax">Fax</Label>
                                            <Controller
                                                name="fax"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="text"
                                                        id="fax"
                                                        placeholder="##########"
                                                        {...field}
                                                        value={field.value ?? ''}
                                                    />
                                                )}
                                                defaultValue={institution.fax ?? ''}
                                            />
                                        </FormGroup>
                                    </div>
                                </div>
                            )}
                            {!editing && (
                                <>
                                    <FormGroup>
                                        <Row>
                                            <Col xs={7}>
                                                <Label>Phone</Label>
                                                <div>
                                                    {formatPhoneNumber(institution?.phone)}{' '}
                                                    {institution.extension ? `ext ${institution.extension}` : ''}
                                                </div>
                                            </Col>
                                            <Col>
                                                <Label>Fax:</Label>
                                                <div>
                                                    {institution?.fax ? formatPhoneNumber(institution?.fax) : 'N/A'}
                                                </div>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                </>
                            )}
                        </div>
                        <FormGroup>
                            <Label for="webSite">Website</Label>
                            {editing && (
                                <Controller
                                    name="webSite"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="text"
                                            id="webSite"
                                            placeholder="Website"
                                            {...field}
                                            value={field.value ?? ''}
                                        />
                                    )}
                                    defaultValue={institution.webSite ?? ''}
                                />
                            )}
                            {!editing && (
                                <div>
                                    {institution.webSite && (
                                        <a
                                            href={`${institution?.webSite?.indexOf('http://') > -1 ? '' : 'http://'}${
                                                institution.webSite
                                            }`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {institution.webSite}&nbsp;<i className={'remixicon-links-line'}></i>
                                        </a>
                                    )}
                                    {!institution.webSite && 'N/A'}
                                </div>
                            )}
                        </FormGroup>
                        {editing && (
                            <FormGroup>
                                <Label for="address">Address</Label>
                                <div>
                                    <Controller
                                        name="address"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                id="address"
                                                placeholder="Address"
                                                {...field}
                                                value={field.value ?? ''}
                                            />
                                        )}
                                        defaultValue={institution.address ?? ''}
                                    />
                                </div>
                                <div className="mt-1">
                                    <Controller
                                        name="address2"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                id="address2"
                                                placeholder="Address 2"
                                                {...field}
                                                value={field.value ?? ''}
                                            />
                                        )}
                                        defaultValue={institution.address2 ?? ''}
                                    />
                                </div>
                                <div className="mt-1">
                                    <Controller
                                        name="address3"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                id="address3"
                                                placeholder="Address 3"
                                                {...field}
                                                value={field.value ?? ''}
                                            />
                                        )}
                                        defaultValue={institution.address3 ?? ''}
                                    />
                                </div>
                                <div className="mt-1">
                                    <Controller
                                        name="address4"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                id="address4"
                                                placeholder="Address 4"
                                                {...field}
                                                value={field.value ?? ''}
                                            />
                                        )}
                                        defaultValue={institution.address4 ?? ''}
                                    />
                                </div>
                                <div className="mt-1">
                                    <div className="d-inline-block w-50">
                                        <Controller
                                            name="city"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="text"
                                                    id="city"
                                                    placeholder="City"
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                            defaultValue={institution.city ?? ''}
                                        />
                                    </div>
                                    <div className="d-inline-block w-50">
                                        {!isInternational && (
                                            <Controller
                                                name="state"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type={'select'}
                                                        id="state"
                                                        placeholder="State"
                                                        {...field}
                                                        value={field.value ?? ''}
                                                    >
                                                        <option value={undefined}>{``}</option>
                                                        {States &&
                                                            States.map((s) => (
                                                                <option key={s.abbreviation} value={s.abbreviation}>
                                                                    {s.abbreviation}
                                                                </option>
                                                            ))}
                                                    </Input>
                                                )}
                                                defaultValue={institution.state ?? ''}
                                            />
                                        )}
                                        {isInternational && (
                                            <Controller
                                                name="state"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="text"
                                                        id="state"
                                                        placeholder="State"
                                                        {...field}
                                                        value={field.value ?? ''}
                                                    />
                                                )}
                                                defaultValue={institution.state ?? ''}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="mt-1 d-flex" style={{ gap: '0.5rem' }}>
                                    <div style={{ flex: '0 0 25%' }}>
                                        <Controller
                                            name="zip"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="text"
                                                    id="zip"
                                                    placeholder={isInternational ? `Postal Code` : `Zip`}
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                            defaultValue={institution.zip ?? ''}
                                        />
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <Controller
                                            name="country"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type={'select'}
                                                    id="country"
                                                    placeholder="Country"
                                                    {...field}
                                                    value={field.value ?? ''}
                                                >
                                                    <option value={undefined}>{``}</option>
                                                    {Countries &&
                                                        Countries.map((c) => (
                                                            <option key={c} value={c}>
                                                                {c}
                                                            </option>
                                                        ))}
                                                </Input>
                                            )}
                                            defaultValue={institution.country ?? ''}
                                        />
                                    </div>
                                </div>
                            </FormGroup>
                        )}
                        {!editing && (
                            <>
                                <Row className="mt-2">
                                    <Col>
                                        <Label>Address</Label>
                                        <div>{institution?.address || ''}</div>
                                        {institution?.address2 && <div>{institution?.address2}</div>}
                                        {institution?.address3 && <div>{institution?.address3}</div>}
                                        {institution?.address4 && <div>{institution?.address4}</div>}
                                        <div>{`${institution?.city || ''}, ${institution?.state || ''} ${
                                            institution?.zip || ''
                                        }`}</div>
                                        <div>{institution?.country || ''}</div>
                                    </Col>
                                </Row>
                                {authService.hasPermission(AmsPermissionClaimType, 'systemadmin') && (
                                    <Row className="mt-4">
                                        <Col>
                                            <Button onClick={() => setEditing(!editing)}>Edit</Button>
                                        </Col>
                                    </Row>
                                )}
                                {(institution.lastEdited || institution.editedBy) && (
                                    <Row className="mt-2">
                                        <Col className="font-italic">
                                            <span>{`Last edited`}</span>
                                            {editedByInfo && <span>{` by ${editedByInfo.name}`}</span>}
                                            {institution.lastEdited && (
                                                <span>{` on ${moment
                                                    .utc(institution.lastEdited)
                                                    .local()
                                                    .format('MM/DD/YYYY [at] hh:mm A')}`}</span>
                                            )}
                                        </Col>
                                    </Row>
                                )}
                            </>
                        )}
                        <Controller
                            name="institutionId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            defaultValue={institution.institutionId ?? ''}
                        />
                        <Controller
                            name="ceoId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            defaultValue={`${institution.ceoId ?? ''}`}
                        />
                        <Controller
                            name="createdBy"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            defaultValue={`${institution.createdBy ?? ''}`}
                        />
                        <Controller
                            name="dateCreated"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            defaultValue={`${institution.dateCreated ?? ''}`}
                        />
                        <Controller
                            name="lastEdited"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            defaultValue={`${institution.lastEdited ?? ''}`}
                        />
                        <Controller
                            name="editedBy"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            defaultValue={`${institution.editedBy ?? ''}`}
                        />
                        <Controller
                            name="preserveBillingInformation"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            defaultValue={`${institution.preserveBillingInformation ?? ''}`}
                        />
                        <Controller
                            name="synchronizeCeoInstitution"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            defaultValue={`${institution.synchronizeCeoInstitution ?? ''}`}
                        />
                        <Controller
                            name="active"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                            defaultValue={`${institution.active ?? ''}`}
                        />

                        {editing && (
                            <>
                                <Row className="mt-3">
                                    <Col className={`d-flex justify-content-between`}>
                                        <div>
                                            <Button className={'mr-2'} type="submit">{`Save`}</Button>
                                            <Button outline onClick={() => setEditing(!editing)}>
                                                {`Cancel`}
                                            </Button>
                                        </div>
                                        <Button type={'button'} color={'danger'} onClick={() => toggleShowRemove()}>
                                            {`Deactivate`}
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Form>
                )}
                {institution && (
                    <Modal isOpen={showNameHistory} toggle={toggleShowNameHistory} centered>
                        <InstitutionNameHistory institutionId={institution.institutionId} />
                    </Modal>
                )}
                <Modal isOpen={showRemove} toggle={toggleShowRemove} centered>
                    <Card className={'mb-0'}>
                        <CardBody>
                            <h4 className={'card-title'}>{`Deactivate Institution`}</h4>
                            <p>
                                {`To confirm the deactivation of this institution, please click the 'Confirm' button below.`}
                            </p>
                        </CardBody>
                        <CardFooter>
                            <Button type={'button'} color={'primary'} className={'mr-2'} onClick={() => onDeactivate()}>
                                {`Confirm`}
                            </Button>
                            <Button type={'button'} color={'secondary'} onClick={() => toggleShowRemove()}>
                                {`Cancel`}
                            </Button>
                        </CardFooter>
                    </Card>
                </Modal>
            </>
        );
    }
};

export default Institution;
